import type { TokenResponse, AuthProviderConfig } from '@auth/domain';
import { track } from '@core/logging';
import { HttpClient } from '@core/services/HttpClient';

/**
 * Get OAuth tokens from identity provider using authorization code
 */
export async function getProviderTokens(params: {
  code: string;
  codeVerifier: string;
  redirectUri: string;
  provider: AuthProviderConfig;
}): Promise<TokenResponse> {
  const { code, codeVerifier, redirectUri, provider } = params;

  track('AuthProviderTokenExchangeInitiated', {
    providerId: provider.id,
  });

  // Create HttpClient instance for the token server
  const httpClient = new HttpClient({
    baseUrl: provider.tokenServerUrl,
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
  });

  // Prepare form data for token exchange
  const tokenData = new URLSearchParams({
    grant_type: 'authorization_code',
    code,
    code_verifier: codeVerifier,
    redirect_uri: redirectUri,
    client_id: provider.clientId,
  });

  try {
    // Use HttpClient for the POST request with built-in error handling and logging
    const tokenResponse = await httpClient.post<TokenResponse>('', tokenData);

    track('AuthProviderTokenExchangeSucceeded', {
      providerId: provider.id,
    });

    return tokenResponse;
  } catch (error) {
    track('AuthProviderTokenExchangeFailed', {
      providerId: provider.id,
      errorMessage: error instanceof Error ? error.message : String(error),
      errorType: error instanceof Error ? error.name : 'unknown',
    });

    // HttpClient already provides detailed error logging and correlation IDs
    throw new Error(`Token exchange failed: ${error instanceof Error ? error.message : String(error)}`);
  }
}
